import { Card, Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { IAllUserNode } from '../../../types/users'
import { ChannelConfigData } from '../../channelConfig/hooks/useChannelConfig'
import { URL as BACKEND_URL } from '../../../gql/client'
import {FormattedMessage, useIntl} from 'react-intl'
import {ONE_USER_MESSAGES as M} from '../oneUserMessages'

// Nombres de producto: iguales en EN y ES.
const PRODUCT_WHATSAPP = 'WhatsApp'
const PRODUCT_POLICYDIAL = 'PolicyDial'

/** URL del callback de estado de Twilio. Izada a helper: el rule marca los
 *  template literals como hijos de JSX. */
const statusCallbackUrl = (baseUrl: string) => `${baseUrl}/webhooks/twilio/status/`

interface Props {
  node: IAllUserNode
  orgConfig: Partial<ChannelConfigData>
  loadingOrgConfig: boolean
  orgId: string | undefined
}

export const OrganizationChannelsTab: React.FC<Props> = ({
  node,
  orgConfig,
  loadingOrgConfig,
  orgId,
}) => {
  const intl = useIntl()
  const WebhookLine = ({ label, url }: { label: string; url: string }) => {
    return (
      <div className='row g-2 mt-2 align-items-center p-2 bg-light border rounded'>
        <span className='fs-8 fw-semibold col-auto'>{label}</span>
        <code className='col text-dark fs-7 text-break'>{url}</code>
        <button
          type='button'
          className='btn btn-sm btn-primary p-1 col-auto'
          onClick={() => {
            navigator.clipboard.writeText(url)
            toast.success(intl.formatMessage(M.ocToastWebhookCopied))
          }}
          title={intl.formatMessage(M.ocCopyWebhook)}
          style={{ minWidth: '28px' }}
        >
          <i className='bi bi-clipboard' />
        </button>
      </div>
    )
  }

  if (!orgId) {
    return (
      <Card className='mb-0'>
        <Card.Body className='text-center py-10'>
          <i className='bi bi-exclamation-triangle fs-1 text-warning mb-3 d-block' />
          <h5 className='text-gray-700'>
            <FormattedMessage {...M.ocNoOrg} />
          </h5>
          <p className='text-muted mb-0'>
            <FormattedMessage {...M.ocNoOrgBody} />
          </p>
        </Card.Body>
      </Card>
    )
  }

  if (loadingOrgConfig) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{ minHeight: 280 }}>
        <Spinner animation='border' variant='primary' />
      </div>
    )
  }

  const baseUrl = BACKEND_URL

  return (
    <>
      <div className='mb-5'>
        <p className='text-muted fs-7 mb-0'>
          <FormattedMessage {...M.ocIntro} />
        </p>
      </div>

      <div className='row g-3'>
        {/* ─── Twilio SMS ─── */}
        <div className='col-md-6'>
          <Card className='h-100'>
            <Card.Body className='p-4'>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <div className='d-flex align-items-center gap-3'>
                  <i className='bi bi-chat-dots fs-3 text-info' />
                  <div>
                    <div className='fw-bold text-gray-900'>
                      <FormattedMessage {...M.ocTwilioSms} />
                    </div>
                    <div className='text-muted fs-8'>
                      <FormattedMessage {...M.ocSmsIntegration} />
                    </div>
                  </div>
                </div>
              </div>
              {orgConfig.twilio_enabled && orgConfig.twilio_phone_number && (
                <>
                  <div className='text-muted fs-8 mb-2'>
                    <FormattedMessage {...M.ocPhoneNumber} />
                  </div>
                  <div className='fw-bold text-gray-900 mb-3'>{orgConfig.twilio_phone_number}</div>
                  {orgConfig.webhook_token && (
                    <>
                      <WebhookLine
                        label='Inbound Messages:'
                        url={`${baseUrl}/webhooks/twilio/sms/${orgConfig.webhook_token}/`}
                      />
                      <div className='row g-2 mt-2 align-items-center p-2 bg-warning border rounded'>
                        <span className='fs-8 fw-semibold col-auto'>
                        <FormattedMessage {...M.ocStatusCallbacks} />
                      </span>
                        <code className='col text-dark fs-7 text-break'>{statusCallbackUrl(baseUrl)}</code>
                        <button
                          type='button'
                          className='btn btn-sm btn-warning p-1 col-auto'
                          onClick={() => {
                            navigator.clipboard.writeText(`${baseUrl}/webhooks/twilio/status/`)
                            toast.success(intl.formatMessage(M.ocToastStatusCopied))
                          }}
                          title={intl.formatMessage(M.ocCopyStatusCallback)}
                          style={{ minWidth: '28px' }}
                        >
                          <i className='bi bi-clipboard' />
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}
              {!orgConfig.twilio_enabled && (
                <div className='text-muted fs-8'>
                    <FormattedMessage {...M.ocNotConfigured} />
                  </div>
              )}
            </Card.Body>
          </Card>
        </div>

        {/* ─── WhatsApp Integration ─── */}
        <div className='col-md-6'>
          <Card className='h-100'>
            <Card.Body className='p-4'>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <div className='d-flex align-items-center gap-3'>
                  <i className='bi bi-whatsapp fs-3 text-success' />
                  <div>
                    <div className='fw-bold text-gray-900'>{PRODUCT_WHATSAPP}</div>
                    <div className='text-muted fs-8'>
                      {orgConfig.whatsapp_provider || intl.formatMessage(M.ocNotConfigured)}
                    </div>
                  </div>
                </div>
              </div>
              {orgConfig.whatsapp_provider && orgConfig.whatsapp_provider !== 'NONE' && (
                <>
                  {orgConfig.whatsapp_provider === 'TWILIO' && orgConfig.twilio_whatsapp_number && (
                    <>
                      <div className='text-muted fs-8 mb-2'>
                    <FormattedMessage {...M.ocWhatsAppNumber} />
                  </div>
                      <div className='fw-bold text-gray-900 mb-3'>{orgConfig.twilio_whatsapp_number}</div>
                      {orgConfig.webhook_token && (
                        <>
                          <WebhookLine
                            label='Inbound Messages:'
                            url={`${baseUrl}/webhooks/twilio/whatsapp/${orgConfig.webhook_token}/`}
                          />
                          <div className='row g-2 mt-2 align-items-center p-2 bg-warning border rounded'>
                            <span className='fs-8 fw-semibold col-auto'>
                        <FormattedMessage {...M.ocStatusCallbacks} />
                      </span>
                            <code className='col text-dark fs-7 text-break'>{statusCallbackUrl(baseUrl)}</code>
                            <button
                              type='button'
                              className='btn btn-sm btn-warning p-1 col-auto'
                              onClick={() => {
                                navigator.clipboard.writeText(`${baseUrl}/webhooks/twilio/status/`)
                                toast.success(intl.formatMessage(M.ocToastStatusCopied))
                              }}
                              title={intl.formatMessage(M.ocCopyStatusCallback)}
                              style={{ minWidth: '28px' }}
                            >
                              <i className='bi bi-clipboard' />
                            </button>
                          </div>
                        </>
                      )}
                    </>
                  )}
                  {orgConfig.whatsapp_provider === 'BOTMAKER' && orgConfig.botmaker_phone_line_number && (
                    <>
                      <div className='text-muted fs-8 mb-2'>
                    <FormattedMessage {...M.ocBotmakerLine} />
                  </div>
                      <div className='fw-bold text-gray-900 mb-3'>{orgConfig.botmaker_phone_line_number}</div>
                      {orgConfig.webhook_token && (
                        <WebhookLine
                          label='Webhook URL (Botmaker):'
                          url={`${baseUrl}/webhooks/botmaker/${orgConfig.webhook_token}/`}
                        />
                      )}
                    </>
                  )}
                  {orgConfig.whatsapp_provider === 'META' && orgConfig.meta_whatsapp_phone_number_id && (
                    <>
                      <div className='text-muted fs-8 mb-2'>
                    <FormattedMessage {...M.ocMetaPhoneId} />
                  </div>
                      <div className='fw-bold text-gray-900 mb-3'>{orgConfig.meta_whatsapp_phone_number_id}</div>
                      {orgConfig.webhook_token && (
                        <WebhookLine
                          label='Webhook URL (Meta):'
                          url={`${baseUrl}/webhooks/meta/${orgConfig.webhook_token}/`}
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </div>

        {/* ─── Email SMTP ─── */}
        <div className='col-md-6'>
          <Card className='h-100'>
            <Card.Body className='p-4'>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <div className='d-flex align-items-center gap-3'>
                  <i className='bi bi-envelope fs-3 text-warning' />
                  <div>
                    <div className='fw-bold text-gray-900'>
                      <FormattedMessage {...M.ocEmailSmtp} />
                    </div>
                    <div className='text-muted fs-8'>
                      <FormattedMessage {...M.ocEmailIntegration} />
                    </div>
                  </div>
                </div>
              </div>
              {orgConfig.email_enabled && orgConfig.email_host && (
                <>
                  <div className='text-muted fs-8 mb-2'>
                    <FormattedMessage {...M.ocSmtpHost} />
                  </div>
                  <div className='fw-bold text-gray-900 mb-1'>{orgConfig.email_host}</div>
                  <div className='text-muted fs-8 mb-3'>
                    {intl.formatMessage(orgConfig.email_use_tls ? M.ocPortTls : M.ocPort, {
                      port: orgConfig.email_port || 587,
                    })}
                  </div>
                  <div className='text-muted fs-8 mb-2'>
                    <FormattedMessage {...M.ocFromAddress} />
                  </div>
                  <div className='fw-bold text-gray-900'>
                    {orgConfig.email_from || intl.formatMessage(M.ocNotSet)}
                  </div>
                </>
              )}
              {!orgConfig.email_enabled && (
                <div className='text-muted fs-8'>
                    <FormattedMessage {...M.ocNotConfigured} />
                  </div>
              )}
            </Card.Body>
          </Card>
        </div>

        {/* ─── PolicyDial ─── */}
        <div className='col-md-6'>
          <Card className='h-100'>
            <Card.Body className='p-4'>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <div className='d-flex align-items-center gap-3'>
                  <img src='/media/tfi/policydial-icon.svg' alt={PRODUCT_POLICYDIAL} style={{ width: '28px', height: '28px' }} />
                  <div>
                    <div className='fw-bold text-gray-900'>{PRODUCT_POLICYDIAL}</div>
                    <div className='text-muted fs-8'>
                      <FormattedMessage {...M.ocVicidialIntegration} />
                    </div>
                  </div>
                </div>
              </div>
              {orgConfig.vicidial_enabled ? (
                <>
                  <div className='text-muted fs-8 mb-2'>
                    <FormattedMessage {...M.ocBaseUrl} />
                  </div>
                  <div className='fw-bold text-gray-900 mb-3'>
                    {orgConfig.vicidial_base_url || intl.formatMessage(M.ocNotSet)}
                  </div>
                  {orgConfig.vicidial_campaign_id && (
                    <>
                      <div className='text-muted fs-8 mb-2'>
                    <FormattedMessage {...M.ocCampaignId} />
                  </div>
                      <div className='fw-bold text-gray-900'>{orgConfig.vicidial_campaign_id}</div>
                    </>
                  )}
                </>
              ) : (
                <div className='text-muted fs-8'>
                    <FormattedMessage {...M.ocNotConfigured} />
                  </div>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>
    </>
  )
}
